import classNames from "classnames";
import { useLayoutEffect, useRef } from "react";
import { useTimelineCtx } from "../ctx";
import {
  TimelineContentWrappingEnum,
  TimelinePositionEnum,
  TimelineStyleEnum,
  type ITimelineTrackSegmentProps,
} from "../types";
import { TimelineMarkerFactory } from "./timeline-marker-factory";

type IProps = ITimelineTrackSegmentProps;

// Shared with the row-level sinusoid: bullet (20) + padding (10) = 40px
// halo per side. Path endpoints match PEAK_Y / TROUGH_Y so bullets sit
// on the curve's peak/trough.
export const BULLET_SIZE = 20;
export const HALO_PADDING = 10;
export const HALO_RADIUS = BULLET_SIZE / 2 + HALO_PADDING;

export const WAVE_HEIGHT = 140;
export const PEAK_Y = HALO_RADIUS;
export const TROUGH_Y = WAVE_HEIGHT - HALO_RADIUS;

export const TimelineHorizontalTrackSegment = ({
  itemIdx,
  editableMarkerSlot,
  editableTextSlot,
  setSettings,
  itemDetails,
}: IProps) => {
  const ctx = useTimelineCtx();
  const {
    isMobile,
    isPdf,
    registerMarkerSize,
    comboSettings: {
      timelinePosition,
      timelineStyle,
      timelineContentWrapping,
      timelineMarkerShape,
      timelineHideLine,
      timelineSnakeOrder,
      timelineMarker,
      timelineSetsPerSlide,
    },
  } = ctx;
  const { isOutside, isLast } = itemDetails;
  const isWideShape = timelineMarkerShape === "arrow-big";
  const hideLineSegment = timelineHideLine || timelineSnakeOrder;

  const trackRef = useRef<HTMLDivElement | null>(null);
  const markerWrapperRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const marker = track.querySelector<HTMLElement>(".timeline-item-marker");
    if (!marker) return;
    const update = () => {
      const markerRect = marker.getBoundingClientRect();
      // Walk up to the grid cell (direct child of the grid container).
      let cell: HTMLElement | null = track.parentElement;
      while (cell?.parentElement) {
        const parentDisplay = getComputedStyle(cell.parentElement).display;
        if (parentDisplay === "grid") break;
        cell = cell.parentElement;
      }
      const cellRect = cell?.getBoundingClientRect();
      const xOffsetInCell = cellRect
        ? markerRect.left - cellRect.left + markerRect.width / 2
        : undefined;
      registerMarkerSize(itemIdx, {
        width: marker.offsetWidth,
        height: marker.offsetHeight,
        xOffsetInCell,
      });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(marker);
    return () => observer.disconnect();
    // Marker DOM is re-mounted on any of these settings — observer must
    // re-attach to the new node.
  }, [
    itemIdx,
    registerMarkerSize,
    timelineMarker,
    timelineStyle,
    timelineMarkerShape,
    timelineHideLine,
  ]);

  const contentPlacement = ctx.comboSettings.timelineContentPlacement;
  const skipOrdering =
    contentPlacement === "alternating" || contentPlacement === "split";
  // Path style only takes effect with alternating placement — that's
  // the only layout where the global sinusoid renders. For split or
  // default placement, render as a regular marker + inline line.
  const isPath =
    timelineStyle === TimelineStyleEnum.Path &&
    contentPlacement === "alternating";
  const isNewLine =
    timelineContentWrapping === TimelineContentWrappingEnum.NewLine;

  // NewLine wraps onto stacked rows, so bullet alternation restarts at
  // every row boundary — each wrapped row begins at the peak (matches the
  // per-block sinusoid, which renders with startAtPeak=true). The carousel
  // is a single continuous line, so it alternates by global index instead
  // — otherwise the alternation resets at each page boundary and two
  // adjacent items land on the same side (peak/peak). Must match
  // timeline-item.tsx alternating content placement.
  const newLinePerRow =
    !timelineSetsPerSlide || timelineSetsPerSlide === "auto"
      ? 3
      : Number(timelineSetsPerSlide);
  const isPeak = isNewLine
    ? (itemIdx % newLinePerRow) % 2 === 0
    : itemIdx % 2 === 0;

  return (
    <div
      ref={trackRef}
      data-is-last={isLast}
      className={classNames({
        "timeline-item-track tw-relative tw-w-full": true,
        "tw-flex tw-min-h-5 tw-flex-row tw-self-center": true,
        "tw-items-center": !isPath,
        "tw-items-start": isPath && isPeak,
        "tw-items-end": isPath && !isPeak,
        "-tw-order-1 tw-mb-4":
          !skipOrdering &&
          !isWideShape &&
          timelinePosition === TimelinePositionEnum.Top,
        "-tw-order-1 tw-mb-6":
          !skipOrdering &&
          isWideShape &&
          timelinePosition === TimelinePositionEnum.Top,
        "tw-order-none tw-my-4":
          !skipOrdering && timelinePosition === TimelinePositionEnum.Middle,
        "tw-order-1 tw-mt-4":
          !skipOrdering &&
          !isWideShape &&
          timelinePosition === TimelinePositionEnum.Bottom,
        "tw-order-1 tw-mt-6":
          !skipOrdering &&
          isWideShape &&
          timelinePosition === TimelinePositionEnum.Bottom,
        // Wide chevron hides fully when out-of-view; line markers fade to 40%.
        "tw-opacity-40":
          !isPdf && !isMobile && !isNewLine && isOutside && !isWideShape,
        "tw-opacity-0":
          !isPdf && !isMobile && !isNewLine && isOutside && isWideShape,
      })}
      style={isPath ? { minHeight: WAVE_HEIGHT } : undefined}
    >
      {isWideShape ? (
        // Wide Items: the chevron IS the track. No separate line / halo.
        <TimelineMarkerFactory
          setSettings={setSettings}
          itemIdx={itemIdx}
          editableMarkerSlot={editableMarkerSlot}
          editableTextSlot={editableTextSlot}
        />
      ) : isPath ? (
        // Halo wrapper — fixed width so the halo CENTER aligns with the
        // sinusoid's bullet x; min-height lets multi-line custom-text
        // grow the row vertically.
        <div
          ref={markerWrapperRef}
          className="tw-relative tw-z-[1] tw-flex tw-items-center tw-justify-center"
          style={{
            width: HALO_RADIUS * 2,
            minHeight: HALO_RADIUS * 2,
            flexShrink: 0,
          }}
        >
          <TimelineMarkerFactory
            setSettings={setSettings}
            itemIdx={itemIdx}
            editableMarkerSlot={editableMarkerSlot}
            editableTextSlot={editableTextSlot}
          />
        </div>
      ) : (
        <>
          <TimelineMarkerFactory
            setSettings={setSettings}
            itemIdx={itemIdx}
            editableMarkerSlot={editableMarkerSlot}
            editableTextSlot={editableTextSlot}
          />
          {!hideLineSegment && (
            <div
              className="timeline__line tw-pointer-events-none tw-h-1 tw-rounded-full"
              style={{
                marginLeft: 16,
                width: isMobile
                  ? "100%"
                  : "calc(100% + var(--timeline-items-gap) / 2 - 16px)",
                marginRight:
                  isLast || isMobile
                    ? 0
                    : "calc(var(--timeline-items-gap) / -2)",
                backgroundColor: "var(--timeline-color)",
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
